import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export type ScheduleOptionStatus = "interest_only" | "open" | "closed" | "hidden";

export interface ScheduleOption {
  id: string;
  class_id: string;
  day_of_week: string;
  start_time: string;
  end_time: string | null;
  status: ScheduleOptionStatus;
  display_label: string;
  interest_threshold: number;
  sort_order: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ScheduleOptionWithCount extends ScheduleOption {
  /** Unique-by-email count of interest submissions tagged for this option. */
  interest_count: number;
}

/**
 * Load unique-by-email interest count for one schedule option.
 *
 * Reads the structured `class_interest_options` selections (one row per
 * option a child was signed up for) filtered by BOTH class identity (DB uuid
 * *and* the public `db:<slug>` id the interest form writes) and
 * `display_label`, so counts never bleed across classes and a child interested
 * in two options counts once in each bucket. Original rows are preserved for
 * audit; only the visible count is deduplicated (by family email + child).
 */
async function countUniqueInterest(
  classIds: string[],
  displayLabel: string,
): Promise<number> {
  const ids = classIds.filter(Boolean);
  if (ids.length === 0) return 0;
  const { data } = await (supabase as any)
    .from("class_interest_options")
    .select("interest:interest_id(email,child_name)")
    .in("class_id", ids)
    .eq("display_label", displayLabel);
  if (!Array.isArray(data)) return 0;
  const seen = new Set<string>();
  for (const r of data) {
    const i = r?.interest;
    const e = (i?.email ?? "").toString().trim().toLowerCase();
    const c = (i?.child_name ?? "").toString().trim().toLowerCase();
    if (e || c) seen.add(`${e}|${c}`);
  }
  return seen.size;
}

/**
 * Load additional schedule options for a class plus deduplicated interest
 * counts per option. Public-safe: anonymous callers see 0 (RLS blocks reads
 * of class_interest for non-admins).
 */
export function useScheduleOptions(dbClassId: string | null | undefined) {
  const [options, setOptions] = useState<ScheduleOptionWithCount[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!dbClassId) {
      setOptions([]);
      return;
    }
    setLoading(true);
    const { data: clsRow } = await (supabase as any)
      .from("classes")
      .select("slug")
      .eq("id", dbClassId)
      .maybeSingle();
    const idsForClass = [dbClassId, clsRow?.slug ? `db:${clsRow.slug}` : ""].filter(Boolean);
    const { data, error } = await (supabase as any)
      .from("class_schedule_options")
      .select("id,class_id,day_of_week,start_time,end_time,status,display_label,interest_threshold,sort_order,created_at,updated_at")
      .eq("class_id", dbClassId)
      .order("sort_order", { ascending: true });
    if (error || !data) {
      setOptions([]);
      setLoading(false);
      return;
    }
    const withCounts: ScheduleOptionWithCount[] = await Promise.all(
      (data as ScheduleOption[]).map(async (opt) => ({
        ...opt,
        interest_count: await countUniqueInterest(idsForClass, opt.display_label),
      })),
    );
    setOptions(withCounts);
    setLoading(false);
  }, [dbClassId]);

  useEffect(() => {
    load();
  }, [load]);

  return { options, loading, reload: load };
}

export interface FutureSessionOverviewRow extends ScheduleOptionWithCount {
  class_title: string;
  class_slug: string | null;
}

/**
 * Admin-only overview across every class. Returns every non-hidden schedule
 * option with its unique-by-email interest count and parent class metadata,
 * sorted goal-reached → closest-to-goal → highest count.
 */
export function useFutureSessionOverview() {
  const [rows, setRows] = useState<FutureSessionOverviewRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data: opts } = await (supabase as any)
      .from("class_schedule_options")
      .select("id,class_id,day_of_week,start_time,end_time,status,display_label,interest_threshold,sort_order,created_at,updated_at")
      .neq("status", "hidden");
    if (!Array.isArray(opts) || opts.length === 0) {
      setRows([]);
      setLoading(false);
      return;
    }
    const classIds = Array.from(new Set(opts.map((o: any) => o.class_id)));
    const { data: classRows } = await (supabase as any)
      .from("classes")
      .select("id,title,slug")
      .in("id", classIds);
    const classMap = new Map<string, { title: string; slug: string | null }>();
    for (const c of (classRows ?? []) as any[]) {
      classMap.set(c.id, { title: c.title, slug: c.slug ?? null });
    }
    const withCounts: FutureSessionOverviewRow[] = await Promise.all(
      (opts as ScheduleOption[]).map(async (o) => {
        const meta = classMap.get(o.class_id);
        return {
          ...o,
          interest_count: await countUniqueInterest(
            [o.class_id, meta?.slug ? `db:${meta.slug}` : ""].filter(Boolean),
            o.display_label,
          ),
          class_title: meta?.title ?? "Class",
          class_slug: meta?.slug ?? null,
        };
      }),
    );
    withCounts.sort((a, b) => {
      const aReached = a.interest_threshold > 0 && a.interest_count >= a.interest_threshold ? 1 : 0;
      const bReached = b.interest_threshold > 0 && b.interest_count >= b.interest_threshold ? 1 : 0;
      if (aReached !== bReached) return bReached - aReached;
      const aPct = a.interest_threshold > 0 ? a.interest_count / a.interest_threshold : 0;
      const bPct = b.interest_threshold > 0 ? b.interest_count / b.interest_threshold : 0;
      if (aPct !== bPct) return bPct - aPct;
      return b.interest_count - a.interest_count;
    });
    setRows(withCounts);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  return { rows, loading, reload: load };
}

export interface InterestedFamily {
  id: string;
  adult_name: string | null;
  email: string | null;
  child_name: string | null;
  child_age: number | null;
  created_at: string;
}

/**
 * Admin: load all interest submissions tied to one schedule option (via
 * class_id + display_label). Duplicates are kept in the raw list; the caller
 * can dedupe for display but the audit history is preserved.
 */
export function useInterestedFamilies(
  dbClassId: string | null | undefined,
  displayLabel: string | null | undefined,
) {
  const [families, setFamilies] = useState<InterestedFamily[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!dbClassId || !displayLabel) {
      setFamilies([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data: clsRow } = await (supabase as any)
        .from("classes")
        .select("slug")
        .eq("id", dbClassId)
        .maybeSingle();
      const ids = [dbClassId, clsRow?.slug ? `db:${clsRow.slug}` : ""].filter(Boolean);
      const { data } = await (supabase as any)
        .from("class_interest_options")
        .select("created_at,interest:interest_id(id,adult_name,email,child_name,child_age,created_at)")
        .in("class_id", ids)
        .eq("display_label", displayLabel)
        .order("created_at", { ascending: false });
      if (!cancelled) {
        const rows = (Array.isArray(data) ? data : [])
          .map((r: any) => r?.interest)
          .filter(Boolean) as InterestedFamily[];
        setFamilies(rows);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [dbClassId, displayLabel]);

  return { families, loading };
}
